export type ConnectInitOptions = {
	workspaceId: string,
	definitionId: string,
	layout?: {
		verticalOffset?: number,
	},
};

type LoaderResponse = {
	url?: unknown,
};

interface IConnectObject {
	init?: (options: ConnectInitOptions) => unknown,
	[key: string]: unknown,
}

interface IConnectWindow extends Window {
	Connect?: IConnectObject,
}

const SCRIPT_ID = 'connect-host-script';
const BODY_CLASS = 'connect-enabled';

// The host script calls this global function when it is ready.
const ON_READY_KEY = '__onConnectHostReady__';

let scriptPromise: Promise<void> | null = null;
let hostReadyPromise: Promise<void> | null = null;
let initDone = false;

function loadScriptOnce(src: string): Promise<void> {
	const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
	if (existing) return Promise.resolve();

	const allowedOrigins = [
		'https://webassistant.onconverse.app',
		'https://webassist.onconverse.app',
		'https://download.ai.onliberty.app',
	];

	const policy = window.trustedTypes?.createPolicy('connect-script-url', {
		createScriptURL: value => {
			const url = new URL(value);

			if (!allowedOrigins.includes(url.origin)) {
				throw new TypeError(`Untrusted Connect script URL: ${url.origin}`);
			}

			return value;
		},
		createHTML: value => value,
	});

	return new Promise((resolve, reject) => {
		const s = document.createElement('script');
		s.id = SCRIPT_ID;
		s.async = true;
		s.crossOrigin = 'anonymous';

		if (policy) {
			(s as unknown as { src: TrustedScriptURL }).src = policy.createScriptURL(src);
		} else {
			s.src = src;
		}

		s.onload = () => resolve();
		s.onerror = () => reject(new Error('Failed to load Connect script'));

		document.head.appendChild(s);
	});
}

async function getHostScriptUrl(workspaceId: string, definitionId: string): Promise<string> {
	const loaderUrl = `https://webassist.onconverse.app/api/v1/loader/workspaces/${encodeURIComponent(
		workspaceId,
	)}/definitions/${encodeURIComponent(definitionId)}`;

	const res = await fetch(loaderUrl);
	const data = (await res.json()) as LoaderResponse;

	if (typeof data?.url !== 'string' || data.url.length === 0) {
		throw new TypeError('Connect loader response missing a valid url');
	}

	return data.url;
}

function ensureHostReadyHook(): Promise<void> {
	if (hostReadyPromise) return hostReadyPromise;

	hostReadyPromise = new Promise(resolve => {
		// Use an 'unknown' bridge to satisfy TS for dynamic window keys.
		const w = window as unknown as Record<string, unknown>;
		const cw = window as unknown as IConnectWindow;

		function onHostReady(host: unknown): void {
			delete w[ON_READY_KEY];

			// The host object becomes window.Connect.
			cw.Connect = host as IConnectObject;

			resolve();
		}

		w[ON_READY_KEY] = onHostReady as unknown;
	});

	return hostReadyPromise;
}

function withTimeout<T>(p: Promise<T>, ms: number, message: string): Promise<T> {
	return new Promise((resolve, reject) => {
		const t = window.setTimeout(() => reject(new Error(message)), ms);

		p.then(v => {
			window.clearTimeout(t);
			resolve(v);
		}).catch(e => {
			window.clearTimeout(t);
			reject(e);
		});
	});
}

export async function enableConnect(options: ConnectInitOptions): Promise<void> {
	document.body.classList.add(BODY_CLASS);

	// Install the global ready hook BEFORE loading the host script.
	const ready = ensureHostReadyHook();

	if (!scriptPromise) {
		scriptPromise = (async () => {
			const url = await getHostScriptUrl(options.workspaceId, options.definitionId);
			await loadScriptOnce(url);
		})();
	}

	// Wait for script load + the host ready callback.
	await scriptPromise;
	await withTimeout(ready, 10000, 'Connect host did not call __onConnectHostReady__');

	if (!initDone) {
		const cw = window as unknown as IConnectWindow;
		const initFn = cw.Connect?.init;

		if (typeof initFn !== 'function') {
			throw new TypeError('window.Connect.init not available');
		}

		initFn(options);
		initDone = true;
	}
}

export function disableConnect(): void {
	// Do not destroy/unmount here. Hide via CSS class instead.
	document.body.classList.remove(BODY_CLASS);
}
